<template>
  <b-overlay :show="loading" rounded="sm">
    <b-row>
      <b-col cols="12" md="12">
        <b-alert show variant="info" class="font-weight-bold">
          <i class="fas fa-info-circle fa-lg mr-2" />
          <span class="lh_30" v-text="$lang.financial_message" />
        </b-alert>
      </b-col>
    </b-row>
    <b-row align-v="center" class="mb-2">
      <b-col cols="12" md="10">
        <b-form-group
          class="mb-0"
          :label="`${$lang.bankName}:`"
          label-cols-md="4"
        >
          <requiredComponent
            slot="label"
            :label="`${$lang.bankName}:`"
            :rules="$v.form.bankNameSelected.$params"
          />
          <selectFind
            :options="bankNameOptions"
            v-model="$v.form.bankNameSelected.$model"
            label="text"
            :isValid="
              $v.form.bankNameSelected.$dirty
                ? !$v.form.bankNameSelected.$error
                : null
            "
            :loading="status.bankName"
            autocomplete="off"
          />
          <b-form-invalid-feedback
            :state="
              $v.form.bankNameSelected.$dirty
                ? !$v.form.bankNameSelected.$error
                : null
            "
          >
            {{ $lang.error_required | replace($lang.bankName) }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row align-v="center" class="mb-2">
      <b-col cols="12" md="10">
        <b-form-group
          class="mb-0"
          :label="`${$lang.cardNumber}:`"
          label-for="cardNumber"
          label-cols-md="4"
        >
          <requiredComponent
            slot="label"
            :label="`${$lang.cardNumber}:`"
            :rules="$v.form.cardNumber.$params"
          />
          <b-form-input
            v-model="$v.form.cardNumber.$model"
            type="text"
            class="dir_ltr"
            id="cardNumber"
            maxLength="16"
            autocomplete="off"
            :state="
              $v.form.cardNumber.$dirty ? !$v.form.cardNumber.$error : null
            "
          />
          <b-form-invalid-feedback
            :state="
              $v.form.cardNumber.$dirty ? !$v.form.cardNumber.$error : null
            "
          >
            <div v-if="!$v.form.cardNumber.required">
              {{ $lang.error_required | replace($lang.cardNumber) }}
            </div>
            <div v-if="!$v.form.cardNumber.numeric">
              {{ $lang.error_numeric | replace($lang.cardNumber) }}
            </div>
            <div
              v-if="
                !$v.form.cardNumber.minLength || !$v.form.cardNumber.maxLength
              "
            >
              {{ $lang.error_length | replace($lang.cardNumber, 16) }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row align-v="center" class="mb-2">
      <b-col cols="12" md="10">
        <ShebaField
          :label="$lang.shebaNumber"
          prefix="IR"
          :defaultValue="form.shebaNumber"
          :getValueState="getShebaNumberState"
          ref="shebaNumberField"
          @updateValue="updateShebaNumber"
        />
      </b-col>
    </b-row>
    <b-row align-v="center" class="mb-2">
      <b-col cols="12" md="10">
        <b-form-group
          class="mb-0"
          :label="`${$lang.description}:`"
          label-for="description"
          label-cols-md="4"
        >
          <requiredComponent
            slot="label"
            :label="`${$lang.description}:`"
            :rules="$v.form.description.$params"
          />
          <b-form-textarea
            v-model="$v.form.description.$model"
            id="description"
            :isValid="
              $v.form.description.$dirty ? !$v.form.description.$error : null
            "
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12" md="10" class="text-right mt-4">
        <b-overlay
          :show="loading"
          spinner-small
          spinner-variant="success"
          class="d-inline-block"
        >
          <b-button
            v-if="isAdmin(permissions)"
            variant="success"
            @click="putPersonFinancial"
            :disabled="loading"
          >
            <mdicon name="check" size="20" />
            {{ $lang.register_info }}
          </b-button>
        </b-overlay>
      </b-col>
    </b-row>
  </b-overlay>
</template>
<script>
import ShebaField from "@/components/base/shebaField.vue";
import { pathOr } from "ramda";
import { renderEumOptions, isAdmin } from "@/shared/utils";
import {
  required,
  minLength,
  maxLength,
  numeric,
} from "vuelidate/lib/validators";

export default {
  name: "person-financial",
  components: {
    ShebaField,
  },
  validations: {
    form: {
      bankNameSelected: {
        required,
      },
      cardNumber: {
        required,
        numeric,
        minLength: minLength(16),
        maxLength: maxLength(16),
      },
      description: {},
    },
  },
  props: {
    code: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      getShebaNumberState: false,
      permissions: [],
      form: {
        bankNameSelected: null,
        cardNumber: null,
        shebaNumber: "",
        description: null,
      },
      status: {
        bankName: false,
      },
    };
  },
  computed: {
    bankNameOptions() {
      return renderEumOptions("bank_name");
    },
  },
  created() {
    this.getPersonFinancial();
  },
  methods: {
    pathOr,
    renderEumOptions,
    isAdmin,
    async getPersonFinancial() {
      this.loading = true;
      const url =
        this.$config.api_url +
        this.$config.profile.person_financial.replace("{0}", this.code);
      try {
        const response = await this.$httpService.get(url);
        const data = pathOr({}, ["data", "data"], response);
        this.permissions = response.data.permissions;
        this.$emit("getPermissions", response.data.permissions);

        this.form.bankNameSelected = {
          value: pathOr(null, ["bank_name", "id"], data),
          text: pathOr(null, ["bank_name", "caption"], data),
        };
        this.form.cardNumber = pathOr(null, ["card_number"], data);
        this.form.shebaNumber = pathOr("", ["sheba_number"], data).replace(
          "IR",
          ""
        );
        this.form.description = pathOr(null, ["description"], data);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loading = false;
      }
    },
    async putPersonFinancial() {
      this.$v.$touch();
      this.getShebaNumberState = !this.getShebaNumberState;

      if (this.$v.$anyError || !this.form.shebaNumber) {
        return;
      }
      this.loading = true;
      let url =
        this.$config.api_url +
        this.$config.profile.person_financial.replace("{0}", this.code);
      try {
        const payload = {
          bank_name_id: pathOr(null, ["value"], this.form.bankNameSelected),
          card_number: this.form.cardNumber,
          sheba_number: "IR" + this.form.shebaNumber,
          description: this.form.description,
        };
        const response = await this.$httpService.put(url, payload);
        this.$notify.success(response.data.message);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loading = false;
      }
    },
    updateShebaNumber(value) {
      this.form.shebaNumber = value;
    },
  },
};
</script>
